import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { databasePropType } from "../../../propTypes";
import { reportingYear } from "../../../constants";
import { formatValue, isIE } from "../../../utilities";
import { withStyles } from "@material-ui/styles";
import AsynchronousDataWidget from "../../widgets/AsynchronousDataWidget";
import PillSubsidiesBox from "./PillSubsidiesBox";
import PrivateSectorPricesByMethodTableWidget from "./PrivateSectorPricesByMethodTableWidget";
import Reference from "../../widgets/Reference";
import ShareOfUsersWithinPrivateSectorByMethodChartWidget from "./ShareOfUsersWithinPrivateSectorByMethodChartWidget";
import ShareWithinPrivateSectorByGniGroupChartWidget from "./ShareWithinPrivateSectorByGniGroupChartWidget";
import SubsidizedPricesAsShareOfNonSubsidizedPriceTableWidget from "./SubsidizedPricesAsShareOfNonSubsidizedPriceTableWidget";
import SectorGraphic from "../infographics/sector/SectorGraphic";
import ThemeIntroductionController from "../layout/ThemeIntroductionController";
import Box from "../../box/Box";
import TextColumns from "../layout/TextColumns";
import TextBox from "../../box/TextBox";
import TextOnly from "../layout/TextOnly";
import MultiFigure from "../layout/MultiFigure";
import FigureOnly from "../layout/FigureOnly";
import SmallFigure from "../layout/SmallFigure";
import ComponentWithIeFallback from "../../widgets/ComponentWithIeFallback";
import staticSectorGraphic from "../../../images/sector_infographic.png";

const styles = {
  sectorInfographic: {
    maxWidth: 960
  },
  staticSectorInfographic: {
    marginTop: "2rem",
    display: "flex",
    justifyContent: "center"
  }
};

const Theme3 = props => {
  const database = props.database;
  const data = props.data;

  return (
    <section id="theme3" className="theme">
      <ThemeIntroductionController
        reference="03"
        title="The Role of the Subsidies in the Private Sector"
      >
        The previous CGA sparked debate over the relative role of public sector
        funding within the private sector, specifically as a result of
        subsidized socially marketed products. This year we build upon new data
        and insights shared by DKT, MSI, PSI, and SHOPS Plus to distinguish
        between users who purchase supplies from private sector entities at
        subsidized prices and those who pay non-subsidized (market-value)
        prices. This allows us to present two different financing models: one
        that is purely market driven, the other that relies on support from the
        public sector. The results indicate that across the 135 LMICs, only a
        small share (12%) of private sector users receive subsidized products.
        In terms of the three GNI groups, the subsidized private sector
        commodities play the largest proportional role within low-income
        countries. Conversely, the subsidized private sector is non-existent in
        upper-middle-income countries.
      </ThemeIntroductionController>

      <TextOnly>
        <p>
          Publication of the 2018 edition of the CGA offered our community
          greater visibility into private sector provision of contraceptive
          supplies. New data from IQVIA revealed the higher prices paid by those
          who secured their supplies through private sector entities. The CGA
          2018 analysis applied these higher prices to a proportion of private
          sector consumption, recognizing that this sector comprised both
          subsidized and non-subsidized products. But it left an incomplete
          picture, in large part because uncertainties remained over the exact
          size of the private market for the subsidized and non-subsidized
          supplies. The 2018 edition of the CGA also treated the social
          marketing sector as a subset of the private sector when in fact the
          work of social marketing agencies straddled both the public and
          private sectors.
        </p>
        <p>
          This edition of the CGA breaks new ground by setting aside the
          distinctions between the social marketing and full commercial sectors,
          focusing instead on the supplies themselves: whether they are sold at
          full commercial price, or whether they benefit from some sort of
          public sector subsidy. For this analysis, who sells in the private
          sector is less important that the cost structure of what they sell.
          Informed by data and insights from SMO actors and other private sector
          experts, the CGA 2019 is now able to tease out those circumstances
          where products received or purchased by individuals from private
          sector facilities were subsidized by either donors or country
          governments. Because of wide diversity in international NGO and SMO
          models for service delivery and product sales, only a subset of the
          supplies they distribute or services they provide fall into the
          classification of subsidized private sector&mdash;that proportion of
          their work where public sector funding reduces the price to users
          within the private sector.
          <Reference marker={5} trailingSpace={false}>
            Because our analysis reflects commodities sold or distributed by
            NGOs and SMOs, we are largely capturing the more traditional model
            of subsidizing commodities. But subsidies can be applied in
            different ways, even if they are not captured here.
          </Reference>
        </p>
      </TextOnly>

      {/*Figure 3.1*/}
      <FigureOnly>
        <Box
          reference="Figure 3.1"
          title="The Path from Funding to Clients"
          backgroundColor="white"
        >
          <p>
            Where a woman gets her contraception doesn't necessarily tell us who
            funded, procured or distributed the commodities.
          </p>

          <p>
            The graphic below represents the numerous paths that connect funding
            to the facilities where clients receive contraception. For NGOs and
            SMOs, the pathways are especially complex, illustrating the variety
            of models for service delivery and product sales.
          </p>

          {!isIE() && (
            <p>
              Explore the various pathways by hovering over the infographic
              below.
            </p>
          )}

          <ComponentWithIeFallback
            fallbackComponent={
              <div className={props.classes.staticSectorInfographic}>
                <img src={staticSectorGraphic} alt="sector infographic" />
              </div>
            }
          >
            <SectorGraphic />
          </ComponentWithIeFallback>
        </Box>
      </FigureOnly>

      {/*Figure 3.2*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ShareOfUsersWithinPrivateSectorByMethodChartWidget
              database={database}
              data={
                data ? data.shareOfUsersWithinPrivateSectorByMethodChart : []
              }
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          Of the 199 million users of contraception who obtain supplies from
          private sector entities, 88% (174 million) purchase non-subsidized
          supplies. Overall, subsidized supplies play only a small role in low-
          and middle-income countries, largely due to the fact that most
          countries have no institutions (such as SMOs) through which subsidized
          products enter the private market. Among those that do, the size of
          the private market&mdash;which itself can vary greatly from country to
          country&mdash;often pales in comparison to the public sector.
        </p>
        <p>
          Of the 135 LMICs, only 49 have contraceptives available at subsidized
          prices within the private sector. Nonetheless, 24.5 million users of
          contraception do currently purchase supplies at subsidized prices from
          the private sector. And, at least for certain contraceptive methods,
          the role of subsidy is not insignificant: 32% of implant users and 23%
          of IUD users who get their method from private sector entities
          purchase subsidized products.
        </p>
      </SmallFigure>

      {/*Box 3.1*/}
      <FigureOnly>
        <PillSubsidiesBox />
      </FigureOnly>

      {/*Figure 3.3*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ShareWithinPrivateSectorByGniGroupChartWidget
              database={database}
              data={
                data ? data.shareWithinPrivateSectorByGniGroupUserChart : []
              }
              id="shareWithinPrivateSectorByGniGroupUserChart"
              dataViewerId="share_within_private_sector_by_gni_group_user_chart"
              reference="Figure 3.3"
              title={`Share of Users within Private Sector, ${reportingYear}`}
              chooserId="figure-3-3"
              tooltipFormatter={function() {
                console.log(this);

                return `${this.series.name} ${
                  this.key
                } Users: <strong>${formatValue(
                  Math.abs(this.y)
                )}</strong><br />Share of Private ${
                  this.key
                } Users: <strong>${formatValue(
                  this.point.proportion,
                  true
                )}</strong>`;
              }}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          When it comes to GNI groups, just as the size of the private sector
          varies, so does the role of subsidized services within the private
          sector. Not surprisingly, the subsidized private sector plays the
          largest role in low-income countries, where 40% of private sector
          users purchase subsidized supplies. Within lower-middle-income
          countries the role of the subsidized private sector shrinks by about
          half, and virtually disappears within upper-middle-income countries.
          However, while the subsidized private sector plays a larger role
          within low-income countries, in absolute terms there are more users of
          subsidized supplies living in lower-middle-income countries (19
          million versus 5.32 million) due to the larger number of users in this
          GNI group.
        </p>
      </SmallFigure>

      <MultiFigure
        largeFigure={
          // Table 3.1
          <PrivateSectorPricesByMethodTableWidget
            onDownloadCsv={() => {
              props.onDownloadCsv("theme3.privateSectorPricesByMethodTable");
            }}
          />
        }
        smallFigure={
          // Table 3.2
          <SubsidizedPricesAsShareOfNonSubsidizedPriceTableWidget
            onDownloadCsv={() => {
              props.onDownloadCsv(
                "theme3.subsidizedPricesAsShareOfNonSubsidizedPriceTable"
              );
            }}
          />
        }
      >
        <h4 className="subsection-heading">
          The Impact of Subsidies on Consumption costs
        </h4>
        <p>
          Drawing on an expanded range of data sources country and regional
          estimates, both subsidized and non-subsidized private sector prices
          have been incorporated into the consumption cost analysis. Whether
          private sector users access subsidized or non-subsidized products has
          important implications for private sector consumption costs, since for
          any method the subsidized price is lower than the non-subsidized
          price. In addition, there is a wide variation in both the subsidized
          and non-subsidized prices across countries and regions.
          <Reference marker={6} trailingSpace={false}>
            These averages were only used when country specific data was
            unavailable.
          </Reference>
        </p>
        <p>
          Comparing the subsidized and non-subsidized private sector price
          within each region gives a sense of the magnitude of the subsidy.
          Overall, injections have the lowest subsidy. Those who consume
          subsidized private sector products pay on average 31% of the
          non-subsidized price. IUDs and pills both have the greatest subsidy,
          with subsidized users paying less than 5% on average of the
          non-subsidized price. Subsidized implants are, on average priced at
          12% of the non-subsidized price, however only a small share of implant
          users purchase their methods from private entities.
        </p>
      </MultiFigure>

      {/*Figure 3.4*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ShareWithinPrivateSectorByGniGroupChartWidget
              database={database}
              data={
                data ? data.shareWithinPrivateSectorByGniGroupCostChart : []
              }
              id="shareWithinPrivateSectorByGniGroupCostChart"
              dataViewerId="share_within_private_sector_by_gni_group_cost_chart"
              reference="Figure 3.4"
              title={`Share of Consumption Costs within Private Sector, ${reportingYear}`}
              chooserId="figure-3-4"
              tooltipFormatter={function() {
                return `${this.series.name} ${
                  this.key
                } Costs: <strong>${formatValue(
                  Math.abs(this.y)
                )}</strong><br />Share of Private ${
                  this.key
                } Costs: <strong>${formatValue(
                  this.point.proportion,
                  true
                )}</strong>`;
              }}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
        figureOnLeft={true}
      >
        <p>
          Across low-and-middle-income countries only 2% of private sector costs
          come from spending on subsidized commodities. While this may seem
          small, it still equates to a total of $46.7 million. And for many of
          the women purchasing these subsidized supplies, especially those in
          the poorest countries, these products may have been unaffordable at
          higher prices.
        </p>
        <p>
          Supplies from private sector entities make up the largest relative
          share within low-income countries. In this GNI group, subsidized
          supplies account for 40% of private sector users but only 16% of
          private sector costs. The discrepancy can be attributed differences in
          prices paid between subsidized and non-subsidized products, and
          differences in method mix.
        </p>
        <p>
          Although the subsidized commodities make up a larger share of private
          sector costs within low-income countries, in absolute terms more than
          three times that amount ($36.6 million) is spent purchasing subsidized
          supplies from the private sector in lower-middle-income countries.
          However, in this GNI group, only 6% of private sector costs derive
          from users purchasing subsidized supplies.
        </p>
        <p>
          In addition to out-of-pocket spending by individuals who purchase
          supplies at subsidized prices from private sector entities, an
          additional $44.2 million is incurred by the public sector to cover the
          subsidy. In this analysis, we include this cost within the total
          public sector consumption costs.
        </p>
      </SmallFigure>

      {/*Box 3.2*/}
      <FigureOnly>
        <TextBox
          reference="Box 3.2"
          title="A Wider Approach to Subsidy"
          subTitle="Looking Beyond Subsidized Commodities"
          large={true}
        >
          <TextColumns>
            <p className="highlight">
              The CGA 2019 analysis of subsidy within the private sector is
              primarily focused on commodities themselves. However, discussions
              within the RHSC community have highlighted other approaches to
              applying subsidies within the private sector. The text below,
              provided by PSI, highlights their latest thinking on moving beyond
              a supply-side subsidy.
            </p>
            <p>
              Supply-side subsidy is frequently used to offset commodity costs
              to increase market inputs and ultimately reduce the price paid for
              commodities and services by the consumer. Subsidized commodities
              are important to the promotion of critical health seeking behavior
              by priority audiences, however, long-term reliance on subsidized
              commodities can impede market evolution when not managed
              thoughtfully.
            </p>
            <p>
              Market development requires a long-term view toward equitable
              growth and adaptation. Subsidy can be targeted at any point within
              a market to encourage critical market functions for growth,
              equity, quality, and sustainability. Decisions about where and how
              to target subsidy should ensure that priority audiences benefit
              equitably and that no individual players across the value chain,
              e.g. brands or providers, are inhibited or crowded out from
              performing key market functions. Market growth and equity often
              require subsidy that targets vulnerable groups by addressing
              specific obstacles that these groups face. Subsidy that reduces
              price across the market may not effectively increase use among
              youth for instance, unless other obstacles for access are
              recognized and addressed. Subsidy might better be used to promote
              youth-friendly services or provider behavior change. Targeting
              subsidy to correct specific market constraints strengthens the
              total market and creates resilience that eventually reduces
              reliance on donors.
            </p>
            <p>
              PSI has estimated that adjusting and increasingly targeting
              subsidy within a cost-recovery approach can move certain modern
              contraceptive markets from extrinsic donor dependency to
              sustainability within the short to medium term. Over that period,
              subsidy would need to be increasingly targeted to encourage new
              users and address obstacles for vulnerable users, including policy
              and the enabling environment.
            </p>
          </TextColumns>
        </TextBox>
      </FigureOnly>
    </section>
  );
};

Theme3.propTypes = {
  database: databasePropType,
  data: PropTypes.object,
  onWidgetChange: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired
};

export default memo(withStyles(styles)(Theme3));
